/*
 * AvailabilityFilters — the period + guard/kind filter bar rendered above the
 * availability table. Pure presentational: all state lives on the parent.
 */
import { X } from "lucide-react";
import { inputClass } from "../Primitives";
import { MultiSelectDropdown } from "../MultiSelectDropdown";
import { KIND_OPTIONS, forwardRange } from "./lib";

const PERIOD_PRESETS = [
  { d: 7, label: "1 Week" },
  { d: 14, label: "2 Weeks" },
  { d: 30, label: "1 Month" },
];

export function AvailabilityFilters({
  range, setRange,
  isOp,
  listedGuards,
  guardFilter, setGuardFilter,
  kindFilter, setKindFilter,
  rowsShown, daysUnderlying,
}) {
  return (
    <div className="border border-[#27272A] bg-[#121212] p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="label-mute mr-1">Period</div>
        {PERIOD_PRESETS.map(({ d, label }) => {
          const target = forwardRange(d);
          const active = range.start === target.start && range.end === target.end;
          return (
            <button
              key={d}
              type="button"
              data-testid={`avail-period-${d}d`}
              onClick={() => setRange(target)}
              className={`text-[10px] uppercase tracking-widest border px-2.5 py-1 transition-colors ${
                active
                  ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5"
                  : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"
              }`}
            >{label}</button>
          );
        })}
        <div className="mx-2 h-6 w-px bg-[#27272A]" />
        <div>
          <div className="label-mute mb-0.5">Start</div>
          <input
            data-testid="avail-range-start"
            type="date"
            className={inputClass}
            style={{ width: 140 }}
            value={range.start}
            onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
          />
        </div>
        <div>
          <div className="label-mute mb-0.5">End</div>
          <input
            data-testid="avail-range-end"
            type="date"
            className={inputClass}
            style={{ width: 140 }}
            value={range.end}
            onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
          />
        </div>
        <div
          className="ml-auto text-xs font-mono text-zinc-500"
          title={`${rowsShown} grouped rows · ${daysUnderlying} underlying day-docs`}
        >
          {rowsShown} rows · {daysUnderlying} days
        </div>
      </div>

      {isOp && (
        <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-[#27272A]">
          <div>
            <div className="label-mute mb-1">Officer</div>
            <MultiSelectDropdown
              testId="filter-avail-guard"
              label={`All guards (${listedGuards.length})`}
              options={listedGuards.map((g) => ({ value: g.id, label: g.name || "Unknown" }))}
              selected={guardFilter}
              onChange={setGuardFilter}
              searchable
              triggerClassName="min-w-[220px]"
              emptyHint="No listed "
            />
          </div>
          <div>
            <div className="label-mute mb-1">Kind</div>
            <MultiSelectDropdown
              testId="filter-avail-kind"
              label="All kinds"
              options={KIND_OPTIONS.map((k) => ({ value: k.value, label: k.label }))}
              selected={kindFilter}
              onChange={setKindFilter}
              triggerClassName="min-w-[180px]"
            />
          </div>
          {(guardFilter.length || kindFilter.length) > 0 && (
            <button
              onClick={() => { setGuardFilter([]); setKindFilter([]); }}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] pb-2"
            >
              <X className="size-3" /> Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
